import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

// styles
import {
    BatchAppovalStyled,
    DividerStyled,
    DenyButtonStyled,
    FullyPublishButtonStyled,
    VisuallyApproveButtonStyled
} from "./styles.js";

// hooks
import { useSelect } from "frontend/components/core/Grid2/GridItemContentTemplates/Select/SelectContext";
import { useAuth } from "frontend/hooks/useAuth";
import { useQueueContext } from "frontend/components/fort/mod/core/Layouts/QueueLayout/QueueContext.js";

// redux
import { UploadApprovalThunks } from "frontend/components/fort/mod/photos/slices/uploadApprovalSlice/index.js";
import { setContentIsOpen } from "frontend/components/fort/mod/photos/slices/selectedBatchSlice/index.js";
import { batchesSlice } from "frontend/components/fort/mod/photos/slices/batchesSlice";
import updateBatch from "frontend/components/fort/mod/photos/slices/batchesSlice/updateBatch";
import { messagesSlice } from "frontend/components/fort/mod/photos/slices/messagesSlice.js";

const BatchAppoval = () => {
    const dispatch = useDispatch();

    const {
        state: { allUploadsSelectedInBatch }
    } = useQueueContext();
    const [allFlagsCleared, setAllFlagsCleared] = useState(false);
    const [selectedUploadIds, setSelectedUploadIds] = useState([]);
    const currentQueue = useSelector(state => state.currentQueue.name);
    const { uploadsSelected, getSelectedUploadIds, deselectAll } = useSelect();
    const { entities: uploadEntities } = useSelector(state => state.uploads);
    const { entities: batchEntities } = useSelector(state => state.batches);
    const selectedBatchId = useSelector(state => state.selectedBatch.id);
    const selectedBatchObj = batchEntities[selectedBatchId];
    const { currentUser } = useAuth();
    const publishingIsEditable = selectedBatchObj?.isEditable?.publishing;

    useEffect(() => {
        setSelectedUploadIds(getSelectedUploadIds(uploadsSelected));
    }, [uploadsSelected]);

    useEffect(() => {
        const selectedUploadsHaveFlags = selectedUploadIds.some(id => {
            let hasFlags = false;
            const upload = uploadEntities[id];

            if (upload.hasRecognizablePeopleOrProperty) {
                hasFlags = true;
            }

            if (upload.editorialUseOnly) {
                hasFlags = true;
            }

            if (upload.autoWarningText) {
                hasFlags = true;
            }

            if (upload.autoNudity || upload.autoViolence) {
                hasFlags = true;
            }

            return hasFlags;
        });

        setAllFlagsCleared(!selectedUploadsHaveFlags);
    }, [selectedUploadIds, uploadEntities]);

    const denyVisualFullyOnClick = (title, thunk) => {
        if (!selectedUploadIds.length) return;
        dispatch(
            messagesSlice.actions.addMessage({
                text: `Processing ${title} request`,
                timed: true,
                timeout: 3000,
                infoType: "info"
            })
        );
        dispatch(
            thunk({
                photoUploadIds: selectedUploadIds,
                selectedBatchObj,
                allUploadsSelectedInBatch,
                currentQueue,
                currentUser
            })
        );

        // Batch Remove Animation only occurs when ALL uploads are selected, and moderator clicks Visually Approve, deny, Fully Publish
        if (allUploadsSelectedInBatch) {
            dispatch(setContentIsOpen(false));
            dispatch(
                updateBatch.isEditable({
                    id: selectedBatchId,
                    changes: {
                        releases: false,
                        photoShoot: false,
                        flags: false,
                        uploadToggleBand: false,
                        uploadFlags: false,
                        uploadTitle: false,
                        uploadDesc: false,
                        publishing: false
                    }
                })
            );

            dispatch(
                batchesSlice.actions.updateOne({
                    id: selectedBatchId,
                    changes: {
                        isFaded: true
                    }
                })
            );
        }
        deselectAll();
    };

    const isDisabledCheck = ({ flags = true } = {}) => {
        let isDisabled =
            selectedUploadIds.length === 0 || !publishingIsEditable;

        if (flags && !allFlagsCleared) {
            isDisabled = false;
        }

        return isDisabled;
    };

    const disablePublishing = isDisabledCheck();

    return (
        <BatchAppovalStyled>
            {/* TODO: remove approved/denied uploads from `modBlurredUploads` in localStorage */}
            <FullyPublishButtonStyled
                onClick={() =>
                    denyVisualFullyOnClick(
                        "Fully Publish",
                        UploadApprovalThunks.fullyPublish
                    )
                }
                value="Fully Publish"
                styleType="outline"
                isDisabled={disablePublishing}
            >
                Fully Publish
            </FullyPublishButtonStyled>
            <DividerStyled />
            <DenyButtonStyled
                onClick={() =>
                    denyVisualFullyOnClick("Deny", UploadApprovalThunks.deny)
                }
                value="Deny"
                isDisabled={isDisabledCheck({ flags: false })}
            >
                Deny
            </DenyButtonStyled>
            <VisuallyApproveButtonStyled
                onClick={() =>
                    denyVisualFullyOnClick(
                        "Visually Approve",
                        UploadApprovalThunks.visuallyPublish
                    )
                }
                value="Visually Approve"
                styleType="cta"
                isDisabled={disablePublishing}
            >
                Visually Approve
            </VisuallyApproveButtonStyled>
        </BatchAppovalStyled>
    );
};

export default BatchAppoval;
